import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, CheckCircle, Target, Users, MessageSquare, 
  Shield, Zap, TrendingUp, AlertTriangle, Calendar,
  Building2, Award, ChevronDown, Sparkles, BarChart3,
  Clock, DollarSign, UserX, XCircle, ArrowDown, Bird
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PartnerStrip } from '../components/PartnerStrip';

export default function AssessmentLanding() {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const startAssessment = () => {
    navigate('/assessment/start');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-slate-950 via-slate-950 to-transparent sm:hidden">
        <Button 
          onClick={startAssessment}
          className="w-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold py-4 text-lg rounded-xl shadow-lg shadow-violet-500/25"
        >
          Take the Free Assessment <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-violet-500/15 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[150px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-600/5 rounded-full blur-[100px]" />
        </div>

        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-20 py-6 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">WE</span>
              </div>
              <span className="text-white font-semibold hidden sm:block">The WE Mentality</span>
            </a>
            <Button 
              onClick={startAssessment}
              variant="outline"
              className="border-violet-500/50 text-violet-300 hover:bg-violet-500/10 hidden sm:flex"
            >
              Take Assessment
            </Button>
          </div>
        </header>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/15 to-cyan-500/15 border border-violet-500/30 mb-8">
            <Sparkles className="h-4 w-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-semibold">The WE Alignment Index™</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            How Aligned Is Your Organization…{' '}
            <span className="bg-gradient-to-r from-violet-400 via-cyan-400 to-violet-400 bg-clip-text text-transparent">
              Really?
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            Misalignment shows up as poor communication, unclear accountability, and inconsistent results.
            <span className="text-white font-medium"> In just 5 minutes</span>, uncover exactly where your team is breaking down.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <Button 
              onClick={startAssessment}
              size="lg"
              className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25 w-full sm:w-auto"
            >
              Take the Free Assessment <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => scrollToSection('how-it-works')}
              variant="outline"
              size="lg"
              className="border-slate-700 text-slate-300 hover:bg-slate-800/50 px-8 py-6 text-lg rounded-xl w-full sm:w-auto"
            >
              See How It Works <ChevronDown className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Micro Text */}
          <p className="text-slate-500 text-sm flex items-center justify-center gap-2">
            <Clock className="h-4 w-4" />
            Takes less than 5 minutes. Instant results.
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="h-6 w-6 text-slate-600" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          TIER 1 ENTRY: FIND YOUR DISC BIRD (60-second mini quiz)
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 px-4" data-testid="disc-bird-funnel-cta">
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-violet-500/10 p-8 sm:p-12">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-violet-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 mb-4">
                  <Bird className="h-4 w-4 text-amber-300" />
                  <span className="text-amber-200 text-xs font-semibold uppercase tracking-wider">60-Second Mini Quiz</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 leading-tight">
                  Not ready for the full assessment?{' '}
                  <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
                    Find Your DISC Bird first.
                  </span>
                </h2>
                <p className="text-base sm:text-lg text-slate-300 mb-5 leading-relaxed">
                  Discover whether you lead like an <strong className="text-orange-300">Eagle</strong>, a <strong className="text-yellow-300">Parrot</strong>, a <strong className="text-cyan-300">Dove</strong>, or an <strong className="text-indigo-300">Owl</strong> — and get a personalized communication guide in under a minute.
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400 mb-2">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-400" />10 quick questions</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-400" />Instant result + PDF guide</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-400" />No commitment</span>
                </div>
              </div>
              <div className="flex flex-col gap-3 md:min-w-[260px]">
                <Button
                  onClick={() => navigate('/find-your-bird')}
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-bold px-8 py-6 text-base rounded-xl shadow-xl shadow-amber-500/25 w-full"
                  data-testid="disc-bird-cta-btn"
                >
                  Find My DISC Bird <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <button
                  onClick={startAssessment}
                  className="text-sm text-slate-400 hover:text-violet-300 underline-offset-4 hover:underline transition-colors text-center"
                  data-testid="disc-bird-skip-link"
                >
                  Or jump to the full alignment assessment →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2: THE REAL PROBLEM
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              You Don't Have a People Problem…
            </h2>
            <p className="text-2xl sm:text-3xl text-violet-400 font-semibold">
              You Have an Alignment Problem.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-slate-300 leading-relaxed">
                Most organizations are not struggling because of talent.
                <span className="text-white font-semibold"> They are struggling because their teams are not aligned.</span>
              </p>
              <div className="space-y-4 text-slate-400">
                <p>Leadership communicates one vision.</p>
                <p>Teams interpret it differently.</p>
                <p className="text-red-400 font-semibold">Execution breaks down.</p>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50">
              <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                Sound Familiar?
              </h3>
              <ul className="space-y-4">
                {[
                  "Meetings that don't lead to action",
                  "Lack of accountability across teams",
                  "Confusion around roles and expectations",
                  "Strong effort but weak results"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-300">
                    <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          VIDEO SECTION (Moved to top for engagement)
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
              See Why Alignment Matters
            </h2>
            <p className="text-slate-400">Watch Eddie explain The Alignment Problem</p>
          </div>

          {/* Video Player */}
          <div className="aspect-video bg-slate-900 rounded-2xl border border-slate-700 overflow-hidden shadow-2xl shadow-violet-500/10">
            <video 
              controls
              poster=""
              className="w-full h-full object-cover"
              preload="metadata"
              data-testid="alignment-video"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_67c91ca0-fff1-4343-8758-c6eb92594ada/artifacts/uqsrbl1h_The_Alignment_Problem_-_WE_Mentality%20%281%29.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3: WHAT YOU'LL DISCOVER
      ═══════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-20 sm:py-28 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              What You'll Discover in <span className="text-cyan-400">5 Minutes</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BarChart3, title: "Your Overall Alignment Score", color: "violet" },
              { icon: Target, title: "Leadership Alignment Gaps", color: "cyan" },
              { icon: MessageSquare, title: "Communication Breakdowns", color: "amber" },
              { icon: Shield, title: "Accountability & Ownership Issues", color: "emerald" },
              { icon: Users, title: "Team Trust & Dynamics Insights", color: "rose" },
              { icon: Zap, title: "Execution Gaps Between Strategy & Results", color: "violet" }
            ].map((item, i) => {
              const Icon = item.icon;
              const colorMap = {
                violet: "from-violet-500/20 to-violet-600/20 border-violet-500/30 text-violet-400",
                cyan: "from-cyan-500/20 to-cyan-600/20 border-cyan-500/30 text-cyan-400",
                amber: "from-amber-500/20 to-amber-600/20 border-amber-500/30 text-amber-400",
                emerald: "from-emerald-500/20 to-emerald-600/20 border-emerald-500/30 text-emerald-400",
                rose: "from-rose-500/20 to-rose-600/20 border-rose-500/30 text-rose-400"
              };
              return (
                <div 
                  key={item.title}
                  className={`p-6 rounded-2xl bg-gradient-to-br ${colorMap[item.color]} border hover:scale-105 transition-transform duration-300`}
                >
                  <Icon className={`h-8 w-8 mb-4 ${colorMap[item.color].split(' ').pop()}`} />
                  <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              onClick={startAssessment}
              size="lg"
              className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25"
            >
              Get Your Alignment Score <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4: RESULTS PREVIEW
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Your Results Will Look Like This
            </h2>
            <p className="text-xl text-slate-400">
              Get a clear snapshot of how your organization is performing across key areas
            </p>
          </div>

          {/* Results Preview Card */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 sm:p-10 border border-slate-700/50 shadow-2xl">
              {/* Overall Score */}
              <div className="text-center mb-10">
                <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Overall Alignment Score</p>
                <div className="relative inline-block">
                  <span className="text-7xl sm:text-8xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                    68%
                  </span>
                </div>
                <p className="text-amber-400 font-semibold mt-2">Moderate Alignment</p>
              </div>

              {/* Category Breakdown */}
              <div className="space-y-4">
                {[
                  { name: "Leadership Alignment", score: 72, status: "Moderate", color: "amber" },
                  { name: "Communication", score: 54, status: "Needs Improvement", color: "red" },
                  { name: "Accountability", score: 48, status: "Low", color: "red" },
                  { name: "Team Dynamics", score: 81, status: "Strong", color: "emerald" },
                  { name: "Execution", score: 62, status: "Inconsistent", color: "amber" }
                ].map((item) => (
                  <div key={item.name} className="flex items-center justify-between gap-4">
                    <span className="text-slate-300 text-sm flex-shrink-0 w-40">{item.name}</span>
                    <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          item.color === 'emerald' ? 'bg-emerald-500' : 
                          item.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                    <span className={`text-xs font-semibold flex-shrink-0 w-24 text-right ${
                      item.color === 'emerald' ? 'text-emerald-400' : 
                      item.color === 'amber' ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-center text-slate-500 mt-6">
              Simple, clear insights you can act on immediately.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 5: WHAT HAPPENS NEXT
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              What Happens After You Take the Assessment
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Receive Your Score", desc: "Get your alignment score instantly", icon: BarChart3 },
              { step: "2", title: "Identify Gaps", desc: "See where your organization is breaking down", icon: Target },
              { step: "3", title: "Review Insights", desc: "Explore leadership and team performance data", icon: TrendingUp },
              { step: "4", title: "Take Action", desc: "Option to schedule a leadership debrief", icon: Calendar }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={item.step} className="relative">
                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 h-full">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xl mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                  {i < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-slate-700" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              onClick={startAssessment}
              size="lg"
              className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25"
            >
              Take the Assessment Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 6: WHO THIS IS FOR
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Building2, text: "Business owners scaling their team" },
              { icon: Award, text: "School leaders and administrators" },
              { icon: Users, text: "HR and operations professionals" },
              { icon: Zap, text: "Organizations struggling with execution" },
              { icon: Target, text: "Leaders who want better performance" },
              { icon: TrendingUp, text: "Teams seeking clarity and alignment" }
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.text}
                  className="flex items-center gap-4 p-5 rounded-xl bg-slate-800/30 border border-slate-700/50 hover:border-violet-500/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-6 w-6 text-violet-400" />
                  </div>
                  <span className="text-slate-200">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SCROLL STOPPER SECTION (HIGH IMPACT)
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-gradient-to-r from-red-500/10 via-slate-950 to-amber-500/10 border-y border-red-500/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 mb-6">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <span className="text-red-300 text-sm font-bold uppercase tracking-wide">Reality Check</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Most Organizations Think They're Aligned…{' '}
            <span className="text-red-400">They're Not.</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            The majority of teams score <span className="text-white font-bold">below 70%</span> when they take this assessment. Where do you stand?
          </p>
          <Button 
            onClick={startAssessment}
            size="lg"
            className="bg-gradient-to-r from-red-600 to-amber-500 hover:from-red-500 hover:to-amber-400 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-red-500/25"
          >
            Find Out Your Score <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 7: WHY THIS MATTERS
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Misalignment Is <span className="text-red-400">Expensive</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-xl text-slate-300 leading-relaxed">
                When your organization is not aligned, it costs you more than you think.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: DollarSign, text: "Lost Productivity", color: "red" },
                  { icon: XCircle, text: "Missed Opportunities", color: "amber" },
                  { icon: UserX, text: "High Turnover", color: "red" },
                  { icon: TrendingUp, text: "Poor Execution", color: "amber" }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={item.text}
                      className={`p-4 rounded-xl border ${
                        item.color === 'red' 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : 'bg-amber-500/10 border-amber-500/30'
                      }`}
                    >
                      <Icon className={`h-6 w-6 mb-2 ${
                        item.color === 'red' ? 'text-red-400' : 'text-amber-400'
                      }`} />
                      <span className="text-white font-medium">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-violet-500/10 to-cyan-500/10 rounded-2xl p-8 border border-violet-500/30">
              <p className="text-2xl sm:text-3xl font-bold text-white leading-relaxed mb-6">
                Alignment is not a luxury.
              </p>
              <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                It is a requirement for growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 9: AUTHORITY + TRUST
      ═══════════════════════════════════════════════════════════════ */}
      <div className="-mx-4">
        <PartnerStrip
          heading="Trusted by Leaders and Organizations"
          subheading="The WE Mentality has worked with organizations, schools, and leaders across Nevada to improve alignment, leadership, and performance."
          limit={10}
        />
      </div>

      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-5xl mx-auto">

          {/* Bio */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl p-8 sm:p-10 border border-slate-700/50">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <span className="text-3xl font-bold text-white">BW</span>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold text-white mb-2">Brandon Ward</h3>
                <p className="text-violet-400 font-medium mb-3">Founder, The WE Mentality</p>
                <p className="text-slate-400">
                  Brandon Ward is a leadership and business development expert who helps organizations align their teams, strengthen culture, and drive measurable results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          TESTIMONIALS PLACEHOLDER (FOR FUTURE)
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              What Leaders Are Saying
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                quote: "Brandon's workshops transformed how our leadership team communicates. The alignment we gained has been a game-changer for our school district.",
                name: "School Administrator",
                org: "Nevada School District"
              },
              {
                quote: "The WE Mentality approach helped us identify gaps we didn't even know existed. Our team execution improved dramatically within 90 days.",
                name: "Operations Director",
                org: "City Government"
              }
            ].map((testimonial) => (
              <div 
                key={testimonial.name}
                className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50"
              >
                <p className="text-slate-300 italic mb-4 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500/30 to-cyan-500/30 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{testimonial.name}</p>
                    <p className="text-slate-500 text-xs">{testimonial.org}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 10: OPTIONAL DEBRIEF OFFER
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Want a Deeper Breakdown of Your Results?
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            Schedule a <span className="text-white font-semibold">Leadership Alignment Debrief</span> and get a detailed walkthrough of your results along with a strategy to improve alignment and performance.
          </p>
          <Button 
            onClick={() => window.open('https://calendly.com/coachbrandonward-1/schedule_with_cbw', '_blank')}
            size="lg"
            variant="outline"
            className="border-violet-500/50 text-violet-300 hover:bg-violet-500/10 px-8 py-6 text-lg rounded-xl"
          >
            <Calendar className="mr-2 h-5 w-5" />
            Book a Leadership Debrief
          </Button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          FINAL CLOSE SECTION
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 sm:py-32 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-r from-violet-500/10 to-cyan-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
            You Can't Fix What You Can't See
          </h2>
          <p className="text-xl sm:text-2xl text-slate-400 mb-10">
            Get clarity. Identify the gaps. Start improving today.
          </p>
          <Button 
            onClick={startAssessment}
            size="lg"
            className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-10 py-7 text-xl rounded-xl shadow-xl shadow-violet-500/30"
          >
            Take the Free Assessment <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
          <p className="text-slate-500 text-sm mt-6 flex items-center justify-center gap-2">
            <Clock className="h-4 w-4" />
            Takes 5 minutes. Instant results.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">WE</span>
            </div>
            <span className="text-slate-400 text-sm">The WE Mentality</span>
          </div>
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} The WE Mentality. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Bottom padding for mobile sticky CTA */}
      <div className="h-24 sm:hidden" />
    </div>
  );
}
